import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import Table from '@mui/joy/Table';
import Alert from '@mui/joy/Alert';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Checkbox from '@mui/joy/Checkbox';
import Chip from '@mui/joy/Chip';
import IconButton from '@mui/joy/IconButton';
import Stack from '@mui/joy/Stack';
import Grid from '@mui/joy/Grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import { bankingAccountsApi } from '../../api/banking-accounts.api';
import type {
  BankingAccount,
  AccountNumberType,
  AccountType,
  HolderIdentificationType,
  AccountCurrency,
  CreateBankingAccountPayload,
} from '../../types/banking-account';

const EMPTY_FORM: CreateBankingAccountPayload = {
  bankName: '',
  accountNumber: '',
  accountNumberType: 'cbu',
  accountType: 'savings',
  alias: '',
  currency: 'ars',
  holderName: '',
  holderIdentificationNumber: '',
  holderIdentificationType: 'cuit',
  isDefault: false,
};

function maskAccountNumber(value: string): string {
  if (value.length <= 4) {
    return '••••';
  }
  return `••••${value.slice(-4)}`;
}

function labelAccountNumberType(v: AccountNumberType): string {
  return v === 'cbu' ? 'CBU (banco)' : 'CVU (billetera)';
}

function labelAccountType(v: AccountType): string {
  const map: Record<AccountType, string> = {
    savings: 'Caja de ahorro',
    checking: 'Cuenta corriente',
    virtual_wallet: 'Billetera virtual',
  };
  return map[v];
}

function labelIdType(v: HolderIdentificationType): string {
  const map: Record<HolderIdentificationType, string> = {
    cuit: 'CUIT',
    cuil: 'CUIL',
    dni: 'DNI',
  };
  return map[v];
}

export const BankingAccountsPanel: React.FC = () => {
  const [accounts, setAccounts] = useState<BankingAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateBankingAccountPayload>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await bankingAccountsApi.list();
      setAccounts(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar las cuentas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = (): void => {
    setError(null);
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setModalOpen(true);
  };

  const openEdit = (a: BankingAccount): void => {
    setError(null);
    setEditingId(a.reference);
    setForm({
      bankName: a.bankName,
      accountNumber: a.accountNumber,
      accountNumberType: a.accountNumberType,
      accountType: a.accountType,
      alias: a.alias ?? '',
      currency: a.currency,
      holderName: a.holderName,
      holderIdentificationNumber: a.holderIdentificationNumber,
      holderIdentificationType: a.holderIdentificationType,
      isDefault: a.isDefault,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (): Promise<void> => {
    if (!form.bankName.trim() || !form.accountNumber.trim() || !form.holderName.trim()) {
      setError('Completá banco, número de cuenta y titular');
      return;
    }
    if (form.accountNumber.replace(/\D/g, '').length !== 22) {
      setError('CBU/CVU debe tener 22 dígitos');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const digitsOnly = form.accountNumber.replace(/\D/g, '');
      const payload: CreateBankingAccountPayload = {
        ...form,
        accountNumber: digitsOnly,
        alias: form.alias?.trim() || undefined,
      };
      if (editingId != null) {
        await bankingAccountsApi.update(editingId, payload);
      } else {
        await bankingAccountsApi.create(payload);
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reference: string): Promise<void> => {
    if (!window.confirm('¿Eliminar esta cuenta bancaria?')) {
      return;
    }
    try {
      setError(null);
      await bankingAccountsApi.remove(reference);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al eliminar');
    }
  };

  const handleSetDefault = async (reference: string): Promise<void> => {
    try {
      setError(null);
      await bankingAccountsApi.setDefault(reference);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al marcar predeterminada');
    }
  };

  return (
    <Sheet variant="outlined" sx={{ borderRadius: 'md', p: 2 }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography level="title-md">Cuentas bancarias</Typography>
            <Typography level="body-sm" sx={{ mt: 0.5, color: 'text.secondary', maxWidth: 560 }}>
              Registrá las cuentas donde recibís pagos. El nombre del titular debe coincidir con el
              destinatario que figura en los comprobantes para validar automáticamente el pago y actualizar el
              estado de la venta.
            </Typography>
          </Box>
          <Button startDecorator={<AddIcon />} onClick={openCreate}>
            Nueva cuenta
          </Button>
        </Box>

        <Alert color="primary" variant="soft" sx={{ borderRadius: 'sm' }}>
          <Typography level="body-sm">
            Usá el mismo nombre de titular que aparece en Mercado Pago, transferencias bancarias o CVU
            (incluye billeteras virtuales argentinas).
          </Typography>
        </Alert>

        {error ? (
          <Alert color="danger" variant="soft">
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            Cargando…
          </Typography>
        ) : accounts.length === 0 ? (
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            No hay cuentas registradas. Agregá al menos una para validar comprobantes.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table
              size="sm"
              stripe="odd"
              hoverRow
              sx={{
                '--TableCell-paddingY': '0.125rem',
                '--TableCell-paddingX': '0.375rem',
                '--unstable_TableCell-height': '1.875rem',
                '& th': { whiteSpace: 'nowrap' },
              }}
            >
              <thead>
                <tr>
                  <th>Banco / plataforma</th>
                  <th>Titular</th>
                  <th>CBU/CVU</th>
                  <th>Alias</th>
                  <th>Tipo</th>
                  <th>Moneda</th>
                  <th>Predeterminada</th>
                  <th style={{ width: 140 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.reference}>
                    <td>
                      <Typography level="body-sm" fontWeight="md">
                        {a.bankName}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-sm">{a.holderName}</Typography>
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        {labelIdType(a.holderIdentificationType)} {a.holderIdentificationNumber}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-sm" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                        {maskAccountNumber(a.accountNumber)}
                      </Typography>
                      <Chip size="sm" variant="soft" sx={{ mt: 0.5 }}>
                        {labelAccountNumberType(a.accountNumberType)}
                      </Chip>
                    </td>
                    <td>
                      <Typography level="body-sm">{a.alias || '—'}</Typography>
                    </td>
                    <td>
                      <Typography level="body-sm">{labelAccountType(a.accountType)}</Typography>
                    </td>
                    <td>
                      <Typography level="body-sm" textTransform="uppercase">
                        {a.currency}
                      </Typography>
                    </td>
                    <td>
                      {a.isDefault ? (
                        <Chip size="sm" color="success" variant="soft">
                          Sí
                        </Chip>
                      ) : (
                        <Typography level="body-sm">—</Typography>
                      )}
                    </td>
                    <td>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="neutral"
                          title="Predeterminada"
                          onClick={() => void handleSetDefault(a.reference)}
                          disabled={a.isDefault}
                        >
                          {a.isDefault ? <StarIcon /> : <StarBorderIcon />}
                        </IconButton>
                        <IconButton size="sm" variant="plain" color="neutral" onClick={() => openEdit(a)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => void handleDelete(a.reference)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        )}
      </Stack>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <ModalDialog
          layout="center"
          sx={{
            width: 'min(100%, 720px)',
            maxWidth: 'min(100vw - 32px, 720px)',
            maxHeight: 'min(90vh, 900px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            p: 0,
          }}
        >
          <Box
            sx={{
              px: 3,
              pt: 2,
              pb: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <ModalClose sx={{ m: 0, top: 12, right: 12 }} />
            <Typography level="title-lg" sx={{ pr: 4 }}>
              {editingId != null ? 'Editar cuenta' : 'Nueva cuenta bancaria'}
            </Typography>
          </Box>
          <Box
            sx={{
              px: 3,
              py: 2,
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
            }}
          >
            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <FormControl required sx={{ width: '100%' }}>
                  <FormLabel>Banco o plataforma</FormLabel>
                  <Input
                    placeholder="Ej: Mercado Pago, Santander, Brubank"
                    value={form.bankName}
                    onChange={(e) => setForm((f) => ({ ...f, bankName: e.target.value }))}
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl required sx={{ width: '100%' }}>
                  <FormLabel>Tipo de número</FormLabel>
                  <Select
                    value={form.accountNumberType}
                    onChange={(_, v) =>
                      v != null && setForm((f) => ({ ...f, accountNumberType: v as AccountNumberType }))
                    }
                  >
                    <Option value="cbu">CBU (cuenta bancaria)</Option>
                    <Option value="cvu">CVU (billetera virtual)</Option>
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl required sx={{ width: '100%' }}>
                  <FormLabel>CBU o CVU (22 dígitos)</FormLabel>
                  <Input
                    placeholder="Solo números"
                    value={form.accountNumber}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        accountNumber: e.target.value.replace(/\D/g, '').slice(0, 22),
                      }))
                    }
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl sx={{ width: '100%' }}>
                  <FormLabel>Alias (opcional)</FormLabel>
                  <Input
                    placeholder="alias.mp"
                    value={form.alias ?? ''}
                    onChange={(e) => setForm((f) => ({ ...f, alias: e.target.value }))}
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl required sx={{ width: '100%' }}>
                  <FormLabel>Tipo de cuenta</FormLabel>
                  <Select
                    value={form.accountType}
                    onChange={(_, v) =>
                      v != null && setForm((f) => ({ ...f, accountType: v as AccountType }))
                    }
                  >
                    <Option value="savings">Caja de ahorro</Option>
                    <Option value="checking">Cuenta corriente</Option>
                    <Option value="virtual_wallet">Billetera virtual</Option>
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl required sx={{ width: '100%' }}>
                  <FormLabel>Moneda</FormLabel>
                  <Select
                    value={form.currency ?? 'ars'}
                    onChange={(_, v) =>
                      v != null && setForm((f) => ({ ...f, currency: v as AccountCurrency }))
                    }
                  >
                    <Option value="ars">ARS</Option>
                    <Option value="usd">USD</Option>
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={12}>
                <FormControl required sx={{ width: '100%' }}>
                  <FormLabel>Nombre del titular</FormLabel>
                  <Input
                    placeholder="Como figura en el comprobante"
                    value={form.holderName}
                    onChange={(e) => setForm((f) => ({ ...f, holderName: e.target.value }))}
                  />
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl required sx={{ width: '100%' }}>
                  <FormLabel>Tipo de documento del titular</FormLabel>
                  <Select
                    value={form.holderIdentificationType}
                    onChange={(_, v) =>
                      v != null &&
                      setForm((f) => ({ ...f, holderIdentificationType: v as HolderIdentificationType }))
                    }
                  >
                    <Option value="cuit">CUIT</Option>
                    <Option value="cuil">CUIL</Option>
                    <Option value="dni">DNI</Option>
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={12} sm={6}>
                <FormControl required sx={{ width: '100%' }}>
                  <FormLabel>Número de documento</FormLabel>
                  <Input
                    value={form.holderIdentificationNumber}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, holderIdentificationNumber: e.target.value }))
                    }
                  />
                </FormControl>
              </Grid>
              <Grid xs={12}>
                <Checkbox
                  label="Marcar como cuenta predeterminada"
                  checked={form.isDefault ?? false}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, isDefault: (e.target as HTMLInputElement).checked }))
                  }
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              px: 3,
              py: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              gap: 1,
              justifyContent: 'flex-end',
              flexShrink: 0,
              bgcolor: 'background.surface',
            }}
          >
            <Button variant="plain" color="neutral" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button loading={saving} onClick={() => void handleSubmit()}>
              Guardar
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </Sheet>
  );
};
